import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { LocalDate } from "@js-joda/core";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Heading } from "components/designV2/heading/heading";
import { Modal } from "components/designV2/modal/modal";
import { Text } from "components/designV2/text/text";
import { TextArea } from "components/designV2/textarea/textarea";
import { ColorV2 } from "config/colors";
import { useWindowScrollProgress } from "hooks/useWindowScrollProgress";
import { ApplicationStatus } from "model/Application";
import type { ApplicationDto } from "model/Application";
import { Api } from "services/api/api.service";
import { useAlertStore } from "stores/alertStore/alertStore";
import { useApplicationStore } from "web-apps/company/stores/applicationStore/applicationStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { GigContract } from "web-apps/company/components/gig/gigApplicants/gigContract.component";
import { GigFormModal } from "web-apps/company/components/gig/gigForm/modal/gigFormModal.component";
import { ApplicationListBulkActions } from "./applicationList.bulkActions.component";
import {
  filterApplicationsByDate,
  filterApplicationsByView,
  groupStaffingApplicationsByShift,
  matchesApplicationFilter,
  resolveStaffingPage,
  shouldContinueStaffingPageFetch,
  sortApplicationsByDate,
} from "./applicationList.display.logic";
import { toApplicationStatusUpdate } from "./applicationStatus.logic";
import type {
  ApplicationFilter,
  ApplicationListView,
} from "./applicationList.filter.logic";
import { ApplicationListFilters } from "./applicationList.filters.component";
import {
  ApplicationLoading,
  ApplicationModalActions,
  ApplicationModalContent,
  ApplicationPageHeader,
  ApplicationSpinner,
  EmptyState,
  ListContainer,
  ListWrapper,
} from "./applicationList.styled";
import { ApplicationShiftRow } from "./applicationShiftRow.component";

interface ApplicationPageParams {
  readonly amount: number;
  readonly companyId: number;
  readonly filter: ApplicationFilter;
  readonly page: number;
  readonly status: ApplicationListView;
}

async function getApplicationPage(
  params: ApplicationPageParams,
): Promise<ApplicationDto[]> {
  if (params.status === "Pending") {
    const response =
      await Api().company.application.getPaginatedApplicationsPending(
        params.companyId,
        params.page,
        params.amount,
      );
    return response.data;
  }
  if (params.filter === "All") {
    const response =
      await Api().company.application.getAllPaginatedApplications(
        params.companyId,
        params.page,
        params.amount,
      );
    return response.data;
  }
  const response = await Api().company.application.getPaginatedApplications(
    params.companyId,
    params.page,
    params.amount,
    params.filter,
  );
  return response.data;
}

export const ApplicationList: React.FC = () => {
  const { t } = useTranslation();
  const [applicationState, applicationActions, applicationDispatch] =
    useApplicationStore();
  const [, , alertDispatch] = useAlertStore();
  const [companyState] = useCompanyStore();
  const [status, setStatus] = useState<ApplicationListView>("Pending");
  const [filter, setFilter] = useState<ApplicationFilter>("All");
  const [selectedDate, setSelectedDate] = useState<LocalDate | null>(null);
  const [activeApplication, setActiveApplication] = useState<number | null>(
    null,
  );
  const [selectedApplications, setSelectedApplications] = useState<
    ApplicationDto[]
  >([]);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [applications, setApplications] = useState<ApplicationDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateGigModal, setShowCreateGigModal] = useState(false);
  const apiState = useRef({ loading: false, completed: false, page: 0 });
  const requestGeneration = useRef(0);
  const filteredApplications = useMemo(
    () =>
      sortApplicationsByDate(
        filterApplicationsByDate({ applications, selectedDate }),
      ),
    [applications, selectedDate],
  );
  const handleDateChange = (date: LocalDate | null): void => {
    setSelectedDate(date);
    setSelectedApplications([]);
  };
  const shiftGroups = useMemo(
    () => groupStaffingApplicationsByShift(filteredApplications),
    [filteredApplications],
  );

  const showFetchError = useCallback((): void => {
    alertDispatch({
      type: "SHOW_ALERT",
      payload: {
        icon: "alert",
        title: t("Alerts.OhNo"),
        message: t("ApplicationsCompany.ApplicationCouldNotBeUpdated"),
      },
    });
  }, [alertDispatch, t]);

  const fetchApplications = useCallback(async (): Promise<void> => {
    const companyId = companyState.company?.id;
    if (!companyId || apiState.current.loading || apiState.current.completed) {
      return;
    }
    setIsLoading(true);
    apiState.current.loading = true;
    const amount = Math.max(1, Math.ceil(window.innerHeight / 56));
    const generation = requestGeneration.current;
    const now = Date.now();
    try {
      const applicationsToAppend: ApplicationDto[] = [];
      let shouldFetchNextPage = true;
      do {
        const currentPage = apiState.current.page;
        const page = await getApplicationPage({
          amount,
          companyId,
          filter,
          page: currentPage,
          status,
        });
        if (generation !== requestGeneration.current) return;
        const resolvedPage = resolveStaffingPage({
          applications: page,
          currentPage,
          pageSize: amount,
        });
        const visiblePageApplications = filterApplicationsByView({
          applications: resolvedPage.visibleApplications,
          now,
          view: status,
        });
        applicationsToAppend.push(...visiblePageApplications);
        const pageComplete = page.length === 0 || resolvedPage.complete;
        apiState.current.completed =
          pageComplete || resolvedPage.nextPage <= currentPage;
        apiState.current.page = resolvedPage.nextPage;
        shouldFetchNextPage = shouldContinueStaffingPageFetch({
          complete: apiState.current.completed,
          currentPage,
          nextPage: resolvedPage.nextPage,
          targetApplicationCount: amount,
          visibleApplicationCount: applicationsToAppend.length,
        });
      } while (shouldFetchNextPage);
      setApplications((current) => [...current, ...applicationsToAppend]);
    } catch {
      if (generation === requestGeneration.current) showFetchError();
    } finally {
      if (generation === requestGeneration.current) {
        setIsLoading(false);
        apiState.current.loading = false;
      }
    }
  }, [companyState.company?.id, filter, showFetchError, status]);

  const fetchApplicationCount = useCallback((): void => {
    const companyId = companyState.company?.id;
    if (!companyId) return;
    Api()
      .company.application.getPendingApplicationsCount(companyId)
      .then((response) => {
        applicationDispatch({
          type: "GET_PENDING_APPLICATIONS_COUNT",
          payload: response.data.value,
        });
      })
      .catch(showFetchError);
  }, [applicationDispatch, companyState.company?.id, showFetchError]);

  const handleApplicationUpdated = useCallback(
    (updatedApplication: ApplicationDto): void => {
      setSelectedApplications((current) =>
        current.filter(
          (application) => application.id !== updatedApplication.id,
        ),
      );
      const shouldKeepUpdatedApplication =
        status !== "Pending" &&
        matchesApplicationFilter({
          application: updatedApplication,
          filter,
        });
      setApplications((current) =>
        !shouldKeepUpdatedApplication
          ? current.filter(
              (application) => application.id !== updatedApplication.id,
            )
          : current.map((application) =>
              application.id === updatedApplication.id
                ? updatedApplication
                : application,
            ),
      );
      fetchApplicationCount();
    },
    [fetchApplicationCount, filter, status],
  );

  useEffect(() => {
    setApplications([]);
    setSelectedApplications([]);
    requestGeneration.current += 1;
    apiState.current = { loading: false, completed: false, page: 0 };
    void fetchApplications();
    fetchApplicationCount();
    return () => {
      requestGeneration.current += 1;
    };
  }, [fetchApplicationCount, fetchApplications]);

  useWindowScrollProgress(fetchApplications);

  const updateMarkedApplications = async (
    nextStatus: ApplicationStatus.CLOSED | ApplicationStatus.OFFERED,
  ): Promise<void> => {
    const companyId = companyState.company?.id;
    if (!companyId || isLoading) return;
    setIsLoading(true);
    try {
      const results = await Promise.allSettled(
        selectedApplications.map((application) =>
          applicationActions.updateApplicationStatus(
            companyId,
            application.id,
            {
              ...application,
              ...toApplicationStatusUpdate(nextStatus),
              ...(nextStatus === ApplicationStatus.CLOSED && {
                declineReason,
              }),
            },
          ),
        ),
      );
      const successCount = results.filter(
        (result) => result.status === "fulfilled",
      ).length;
      if (successCount === results.length && successCount > 0) {
        alertDispatch({
          type: "SHOW_ALERT",
          payload: {
            icon: "checkmark",
            title: t(
              nextStatus === ApplicationStatus.OFFERED
                ? "ApplicationsCompany.Assigned"
                : "ApplicationsCompany.Rejected",
            ),
            message: t(
              nextStatus === ApplicationStatus.OFFERED
                ? "ApplicationsCompany.ApplicantHasBeenAssignedGig"
                : "ApplicationsCompany.ApplicationHasBeenRejected",
            ),
          },
        });
      } else {
        showFetchError();
      }
      setShowDeclineModal(false);
      setDeclineReason("");
      setSelectedApplications([]);
      setApplications([]);
      requestGeneration.current += 1;
      apiState.current = { loading: false, completed: false, page: 0 };
      await fetchApplications();
      fetchApplicationCount();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <ApplicationPageHeader>
        <Heading level="h2">{t("ApplicationsCompany.Recruit")}</Heading>
        <PrimaryButton
          label={t("ApplicationsCompany.RecruitWorkers")}
          iconName="v2/plus"
          onClick={() => setShowCreateGigModal(true)}
        />
      </ApplicationPageHeader>
      <ApplicationListFilters
        applicationsCount={applicationState.applicationsCount}
        filter={filter}
        selectedDate={selectedDate}
        status={status}
        onDateChange={handleDateChange}
        onFilterChange={setFilter}
        onStatusChange={setStatus}
      />
      <ListContainer>
        <ListWrapper $loading={isLoading}>
          {shiftGroups.map((group) => (
            <ApplicationShiftRow
              key={group.gigId}
              applications={group.applications}
              handleShowContract={(selectedApplication) =>
                setActiveApplication(selectedApplication.id)
              }
              isPendingView={status === "Pending"}
              onApplicationUpdated={handleApplicationUpdated}
              selectedApplications={selectedApplications}
              setSelectedApplications={setSelectedApplications}
            />
          ))}
          {!filteredApplications.length && !isLoading && (
            <EmptyState typography="b2" color={ColorV2.Blue4}>
              {t("ApplicationsCompany.YouHaveNoApplications")}
            </EmptyState>
          )}
        </ListWrapper>
      </ListContainer>
      <ApplicationListBulkActions
        isLoading={isLoading}
        selectedCount={selectedApplications.length}
        onAssign={() => {
          void updateMarkedApplications(ApplicationStatus.OFFERED);
        }}
        onReject={() => setShowDeclineModal(true)}
      />
      <Modal
        isOpen={activeApplication !== null}
        label={t("GigsWorker.Contract")}
        onClose={() => setActiveApplication(null)}
        size="mediumSlim"
      >
        <GigContract
          applicationId={activeApplication}
          closeContract={() => setActiveApplication(null)}
          showCloseAction={false}
        />
      </Modal>
      <Modal
        isOpen={showDeclineModal}
        label={t("GigsCompany.Reject")}
        onClose={() => setShowDeclineModal(false)}
        size="small"
        footer={
          <ApplicationModalActions>
            <SecondaryButton
              label={t("GigsCompany.Discard")}
              onClick={() => setShowDeclineModal(false)}
              disabled={isLoading}
            />
            <PrimaryButton
              label={t("GigsCompany.Reject")}
              styleOverrides={{
                activeBackgroundColor: ColorV2.DeclinedRed,
                backgroundColor: ColorV2.NegativeRed,
                hoverBackgroundColor: ColorV2.DeclinedRed,
              }}
              disabled={isLoading}
              isLoading={isLoading}
              onClick={() => {
                void updateMarkedApplications(ApplicationStatus.CLOSED);
              }}
            />
          </ApplicationModalActions>
        }
      >
        <ApplicationModalContent>
          <Text typography="h5" color={ColorV2.Blue1}>
            {selectedApplications.length > 1
              ? t("GigsCompany.YouAreAboutToRejectApplications", {
                  number: selectedApplications.length,
                })
              : t("GigsCompany.YouAreAboutToReject1Application")}
          </Text>
          <Text typography="b2" color={ColorV2.Blue3}>
            {t("GigsCompany.ThisCannotBeUndone")}
          </Text>
          <TextArea
            label={t("GigsCompany.NoteToApplicant")}
            placeholder={t("GigsCompany.SendANote")}
            rows={5}
            value={declineReason}
            onChange={setDeclineReason}
          />
        </ApplicationModalContent>
      </Modal>
      {isLoading && (
        <ApplicationLoading>
          <ApplicationSpinner aria-label={t("General.Loading")} role="status" />
        </ApplicationLoading>
      )}
      {showCreateGigModal && (
        <GigFormModal
          showGigFormModal={showCreateGigModal}
          setShowGigFormModal={setShowCreateGigModal}
          initialTab="Recruit"
        />
      )}
    </>
  );
};
